import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  clearSession,
  homeFor,
  isAuthenticated,
  needsReauthSoon,
  REAUTH_BUFFER_SECONDS,
  secondsUntilExpiry,
  userKind,
  type UserKind,
} from '../auth'

/** Hand the visitor to /login, remembering where to come back to. Runs during
 *  render, so blocked storage must not throw: the visitor still reaches /login,
 *  they just won't be bounced back afterwards. */
function rememberReturn(location: { pathname: string; search: string }, reason?: 'expiring') {
  if (reason) clearSession()
  try {
    if (reason) sessionStorage.setItem('auth.reason', reason)
    sessionStorage.setItem('auth.returnTo', location.pathname + location.search)
  } catch {
    // Blocked storage: no return-to / reason, but the redirect still happens.
  }
}

/** Route guard: bounces unauthenticated visitors to `/login`, preserving the
 * current path in sessionStorage so the post-login redirect lands them back
 * where they started. Pulled out of App.tsx into its own module so unit tests
 * can render it directly.
 *
 * Also forces a *proactive* re-login when a still-valid token will expire within
 * the hour (`needsReauthSoon`). Intake/proposal pages can take longer than an
 * hour on a single screen, so rather than let someone start on a near-dead token
 * and lose their work to a mid-edit 401, we send them to sign in again now (for a
 * fresh full-length token) before they begin. The stale token is cleared so the
 * login page starts clean, and `auth.reason` tells Login why.
 *
 * Redirect decisions are made ONLY on entry or on a navigation: the "is the
 * token still valid" answer is latched per `location.key`, so no other
 * re-render (the expiry timer, a tab becoming visible, another tab's sign-in)
 * can move the consultant off the page. The near-expiry redirect is entry-only
 * — it used to run on every navigation too, which re-logged active consultants
 * at whatever navigation followed the hour mark. Mid-session, a timer
 * (re-checked on `visibilitychange`, since background tabs throttle timers,
 * and on `storage`, so another tab's fresh sign-in clears it) shows a
 * non-blocking banner once the token enters the buffer — or has already
 * expired while the tab slept. The consultant saves, then signs in from the
 * banner. A token that has actually expired redirects at the next navigation,
 * and an API 401 (`handleUnauthorized`) remains the last-resort backstop. */
export function RequireAuth({
  children,
  kind = 'staff',
}: {
  children: React.ReactNode
  /** Which session kind this tree is for. A signed-in user of the OTHER kind
   *  is sent to their own home (a customer to /portal, staff to /) — the
   *  server refuses them anyway; this just avoids a page of 403s. */
  kind?: UserKind
}) {
  const location = useLocation()
  const [expiringOnEntry] = useState(() => isAuthenticated() && needsReauthSoon())
  const authed = useLatchedPerLocation(location.key, isAuthenticated)
  const expiringNow = useExpiringSoon(!expiringOnEntry && authed)
  if (expiringOnEntry || !authed) {
    rememberReturn(location, expiringOnEntry ? 'expiring' : undefined)
    return <Navigate to="/login" replace />
  }
  if (userKind() !== kind) {
    return <Navigate to={homeFor()} replace />
  }
  return (
    <>
      {expiringNow && <SessionExpiringBanner />}
      {children}
    </>
  )
}

/** `compute()` evaluated once per `key` — later renders under the same key
 *  reuse the answer. */
function useLatchedPerLocation<T>(key: string, compute: () => T): T {
  const latch = useRef<{ key: string; value: T } | null>(null)
  if (latch.current === null || latch.current.key !== key) {
    latch.current = { key, value: compute() }
  }
  return latch.current.value
}

/** setTimeout's delay is a signed 32-bit int; longer delays fire immediately. */
const MAX_TIMER_MS = 2 ** 31 - 1

/** True while the stored token is inside the re-auth buffer (or past it).
 *  Scheduled for the moment it enters, and recomputed on `visibilitychange`
 *  and `storage` (another tab signing in replaces the token). */
function useExpiringSoon(enabled: boolean): boolean {
  const [expiring, setExpiring] = useState(false)
  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const check = () => {
      clearTimeout(timer)
      const soon = needsReauthSoon()
      setExpiring(soon)
      if (soon) return
      const remaining = secondsUntilExpiry() ?? 0
      const delayMs = (remaining - REAUTH_BUFFER_SECONDS) * 1000 + 1000
      timer = setTimeout(check, Math.min(delayMs, MAX_TIMER_MS))
    }
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') check()
    }
    check()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('storage', check)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('storage', check)
    }
  }, [enabled])
  return enabled && expiring
}

function SessionExpiringBanner() {
  const location = useLocation()
  const navigate = useNavigate()
  const ended = (secondsUntilExpiry() ?? 0) <= 0
  const signIn = () => {
    rememberReturn(location, 'expiring')
    navigate('/login', { replace: true })
  }
  return (
    <div
      role="status"
      data-testid="session-expiring-banner"
      style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.5rem 1rem',
        background: 'var(--warn-soft)',
        borderBottom: '1px solid var(--warn-border)',
        color: 'var(--warn)',
        fontSize: '0.85rem',
      }}
    >
      <span>
        {ended
          ? 'Your session has ended. Copy anything unsaved, then sign in again.'
          : "Your session ends within the hour. Save what you're working on, then sign in again."}
      </span>
      <button type="button" onClick={signIn} data-testid="session-expiring-signin">
        Sign in again
      </button>
    </div>
  )
}
